'use client';

import {
  getListingSearchSignatureWithoutPagination,
  pushFilterInteractionForHousingListIfSearchChanged,
  type ObjectSearchSource,
} from '@/lib/analytics';
import { FiltersType, SearchListFilters, UnitsMultilevelList } from '@/types';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

const FILTER_INTERACTION_DEBOUNCE_MS = 7000;

export type UseHousingListFilterInteractionAnalyticsParams = {
  isProfileReady: boolean;
  isFiltersReady: boolean;
  requestFilters: SearchListFilters | undefined;
  filters: FiltersType;
  preparedFilters: FiltersType;
  unitList: UnitsMultilevelList | undefined;
  searchSource: ObjectSearchSource;
};

/**
 * Debounced `filter_interaction` pushes when the housing search request body changes
 * (excluding pagination). Uses refs so the delayed callback sees the latest filters.
 */
export function useHousingListFilterInteractionAnalytics({
  isProfileReady,
  isFiltersReady,
  requestFilters,
  filters,
  preparedFilters,
  unitList,
  searchSource,
}: UseHousingListFilterInteractionAnalyticsParams) {
  const filtersRef = useRef(filters);
  const preparedFiltersRef = useRef(preparedFilters);
  const unitListRef = useRef(unitList);
  const requestFiltersRef = useRef(requestFilters);
  const searchSourceRef = useRef(searchSource);

  useEffect(() => {
    filtersRef.current = filters;
    preparedFiltersRef.current = preparedFilters;
    unitListRef.current = unitList;
    requestFiltersRef.current = requestFilters;
    searchSourceRef.current = searchSource;
  });

  const listingSearchSignatureRef = useRef<string | null>(null);

  const debouncedPushFilterInteraction = useMemo(
    () =>
      debounce(() => {
        if (!isProfileReady || !isFiltersReady) return;
        pushFilterInteractionForHousingListIfSearchChanged(
          listingSearchSignatureRef,
          requestFiltersRef.current,
          filtersRef.current,
          preparedFiltersRef.current,
          unitListRef.current,
          searchSourceRef.current,
        );
      }, FILTER_INTERACTION_DEBOUNCE_MS),
    [isProfileReady, isFiltersReady],
  );

  useEffect(
    () => () => {
      debouncedPushFilterInteraction.cancel();
    },
    [debouncedPushFilterInteraction],
  );

  useEffect(() => {
    if (!isProfileReady || !isFiltersReady) return;

    const sig = getListingSearchSignatureWithoutPagination(requestFilters);
    if (sig === null) return;

    if (listingSearchSignatureRef.current === null) {
      listingSearchSignatureRef.current = sig;
      return;
    }

    if (listingSearchSignatureRef.current === sig) return;
    debouncedPushFilterInteraction();
  }, [requestFilters, isProfileReady, isFiltersReady, debouncedPushFilterInteraction]);
}
